import { CLIENT_ENDPOINTS } from "@bafe/config";
import { Card, CopyField, Field, InlineNotice, Section, Select } from "@bafe/ui";
import { useState } from "react";

export interface MailServerSettings {
	imap: { host: string; port: number; security: string };
	smtp: Array<{ host: string; port: number; security: string }>;
	jmap?: { url: string } | undefined;
}

/**
 * Only encrypted endpoints are published. There is no plaintext fallback and
 * no POP3 (spec §25).
 */
const DEFAULT_SETTINGS: MailServerSettings = {
	imap: CLIENT_ENDPOINTS.imap,
	smtp: [CLIENT_ENDPOINTS.submissionImplicit, CLIENT_ENDPOINTS.submissionStartTls],
	jmap: CLIENT_ENDPOINTS.jmap,
};

const CLIENTS = [
	{
		id: "iphone",
		label: "iPhone or iPad",
		steps: [
			"Open Settings, then Apps, then Mail, then Mail Accounts.",
			"Choose Add Account, then Other, then Add Mail Account.",
			"Enter your name, your full email address and your mailbox password.",
			"Choose IMAP, then enter the incoming and outgoing settings below.",
		],
	},
	{
		id: "android",
		label: "Android",
		steps: [
			"Open your mail app and choose Add account, then Other.",
			"Enter your full email address and mailbox password.",
			"Choose Personal (IMAP) when asked for the account type.",
			"Enter the incoming and outgoing settings below.",
		],
	},
	{
		id: "apple-mail",
		label: "Apple Mail",
		steps: [
			"Open Mail, then Settings, then Accounts.",
			"Add an account and choose Other Mail Account.",
			"Enter your name, full email address and mailbox password.",
			"When Mail asks for server details, enter the settings below.",
		],
	},
	{
		id: "outlook",
		label: "Outlook",
		steps: [
			"Open Outlook, then File, then Add Account.",
			"Enter your full email address and choose Advanced options, then set it up manually.",
			"Choose IMAP as the account type.",
			"Enter the incoming and outgoing settings below, then your mailbox password.",
		],
	},
	{
		id: "thunderbird",
		label: "Thunderbird",
		steps: [
			"Open Account Settings, then Account Actions, then Add Mail Account.",
			"Enter your name, full email address and mailbox password.",
			"Choose Configure manually and select IMAP.",
			"Enter the incoming and outgoing settings below.",
		],
	},
	{
		id: "other",
		label: "Another mail app",
		steps: [
			"Choose IMAP when the app asks how to connect.",
			"Use your full email address as the username.",
			"Enter the incoming and outgoing settings below.",
		],
	},
] as const;

export interface DeviceSetupPanelProps {
	emailAddress: string;
	username?: string;
	settings?: MailServerSettings;
}

export function DeviceSetupPanel({ emailAddress, username, settings }: DeviceSetupPanelProps) {
	const [clientId, setClientId] = useState<string>(CLIENTS[0].id);
	const resolved = settings ?? DEFAULT_SETTINGS;
	const client = CLIENTS.find((item) => item.id === clientId) ?? CLIENTS[0];
	const account = username ?? emailAddress;

	return (
		<Section
			title="Set up on another device"
			description="Key works with any mail app that supports IMAP. Pick your app for the exact steps."
		>
			<Field label="Which app are you setting up?">
				{({ id, describedBy }) => (
					<Select
						id={id}
						aria-describedby={describedBy}
						value={clientId}
						onChange={(event) => setClientId(event.target.value)}
						className="sm:max-w-xs"
					>
						{CLIENTS.map((item) => (
							<option key={item.id} value={item.id}>
								{item.label}
							</option>
						))}
					</Select>
				)}
			</Field>

			<Card>
				<ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-(--text-secondary)">
					{client.steps.map((step) => (
						<li key={step}>{step}</li>
					))}
				</ol>
			</Card>

			<div className="grid gap-4 lg:grid-cols-2">
				<Card className="flex flex-col gap-3">
					<div className="flex flex-col gap-0.5">
						<h3 className="text-sm font-semibold text-(--text-primary)">Incoming mail (IMAP)</h3>
						<p className="text-sm text-(--text-secondary)">Where your mail app reads your messages.</p>
					</div>
					<SettingRow label="Email address" value={emailAddress} />
					<SettingRow label="Username" value={account} />
					<SettingRow label="Server" value={resolved.imap.host} />
					<SettingRow label="Port" value={String(resolved.imap.port)} />
					<SettingRow label="Security" value={resolved.imap.security} />
				</Card>

				<Card className="flex flex-col gap-3">
					<div className="flex flex-col gap-0.5">
						<h3 className="text-sm font-semibold text-(--text-primary)">Outgoing mail (SMTP)</h3>
						<p className="text-sm text-(--text-secondary)">
							Use the first option. The second is there for apps that only support STARTTLS.
						</p>
					</div>
					{resolved.smtp.map((server, index) => (
						<div key={`${server.host}:${server.port}`} className="flex flex-col gap-3">
							{index > 0 && <hr className="border-(--border-subtle)" />}
							<SettingRow label="Server" value={server.host} />
							<SettingRow label="Port" value={String(server.port)} />
							<SettingRow label="Security" value={server.security} />
						</div>
					))}
					<SettingRow label="Username" value={account} />
					<p className="text-xs text-(--text-muted)">
						Outgoing mail always requires authentication. Use the same password as your mailbox.
					</p>
				</Card>
			</div>

			{resolved.jmap && (
				<Card className="flex flex-col gap-3">
					<div className="flex flex-col gap-0.5">
						<h3 className="text-sm font-semibold text-(--text-primary)">Automatic setup</h3>
						<p className="text-sm text-(--text-secondary)">
							Most apps find these settings on their own once you enter your email address. This is the
							address they use.
						</p>
					</div>
					<SettingRow label="Discovery address" value={resolved.jmap.url} />
				</Card>
			)}

			<InlineNotice tone="info" title="Your password">
				Use the mailbox password, not your Key account password. If you do not have it, reset the mailbox
				password from the Security tab.
			</InlineNotice>
		</Section>
	);
}

function SettingRow({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-xs font-medium text-(--text-muted)">{label}</span>
			<CopyField value={value} label={label} />
		</div>
	);
}
